import Kedo from '@/Kedo';
import {
    ChartDataItem,
    LineChartDataItem,
    BarChartDataItem,
    DefFieldList,
    ChartColorConfig,
} from '../types';

interface RawDataItem {
    '1': string | number;
    '2': string | number | null;
    yearAnddate?: string | null;
    summary?: string | null;
    summary2?: string | null;
}

interface ListField {
    def_field: {
        def_field_lists: DefFieldList[];
    };
}

export const transformToPieData = (
    data: RawDataItem[],
    listField: ListField | null,
    kedo: Kedo
): ChartDataItem[] => {
    if (!data || !listField) return [];

    const defFieldLists = listField.def_field?.def_field_lists ?? [];
    const pieData: ChartDataItem[] = [];

    const unknownItems = data.find((item) => item['2'] === null);
    if (unknownItems) {
        pieData.push({
            id: kedo.t('No value'),
            label: kedo.t('No value'),
            value: parseInt(String(unknownItems['1']), 10),
        });
    }

    defFieldLists.forEach((listItem) => {
        const trans = kedo.env().translateItem(listItem, 'def_field_list', true);
        const dataItem = data.find((d) => parseInt(String(d['2']), 10) === listItem.id);

        if (dataItem) {
            pieData.push({
                id: trans?.length > 0 ? trans : kedo.t('No value'),
                label: trans?.length > 0 ? trans : kedo.t('No value'),
                value: parseInt(String(dataItem['1']), 10),
                color: listItem.color ?? '#F5F5F5',
            });
        }
    });

    return pieData;
};

export const transformToLineData = (
    data: RawDataItem[],
    listField: ListField | null,
    kedo: Kedo
): LineChartDataItem[] => {
    if (!data || !listField) return [];

    const defFieldLists = listField.def_field?.def_field_lists ?? [];
    const lineData: LineChartDataItem[] = [];

    defFieldLists.forEach((listItem) => {
        const listTrans = kedo.env().translateItem(listItem, 'def_field_list', true);
        const listData: { x: string | number; y: number }[] = [];

        data.forEach((dataItem) => {
            if (dataItem['2'] !== null && parseInt(String(dataItem['2']), 10) === listItem.id) {
                listData.push({
                    x: dataItem.yearAnddate ?? 'other',
                    y: parseInt(String(dataItem['1']), 10),
                });
            }
        });

        lineData.push({
            id: listTrans?.length > 0 ? listTrans : kedo.t('No value'),
            data: listData,
            color: listItem.color ?? '#F5F5F5',
        });
    });

    const unknownListData: { x: string | number; y: number }[] = [];
    data.forEach((dataItem) => {
        const itemFound = defFieldLists.some(
            (listItem) => parseInt(String(dataItem['2']), 10) === listItem.id
        );

        if (!itemFound) {
            unknownListData.push({
                x: dataItem.yearAnddate ?? 'other',
                y: parseInt(String(dataItem['1']), 10),
            });
        }
    });

    if (unknownListData.length > 0) {
        lineData.push({
            id: kedo.t('Unknown'),
            data: unknownListData,
        });
    }

    return lineData;
};

export const transformToBarData = (
    data: RawDataItem[],
    listField: ListField | null,
    kedo: Kedo
): BarChartDataItem[] => {
    if (!data || !listField) return [];

    const defFieldLists = listField.def_field?.def_field_lists ?? [];
    const dates = getUniqueDates(data);
    const results: BarChartDataItem[] = [];

    dates.forEach((date) => {
        const newResult: BarChartDataItem = { period: date };

        defFieldLists.forEach((listItem) => {
            const keyName = kedo.env().translateItem(listItem, 'def_field_list');
            const keyColor = listItem.color ? `${keyName}Color` : null;
            let periodTotal = 0;

            for (const d of data) {
                if (
                    d.yearAnddate !== null &&
                    d.yearAnddate?.toString() === date &&
                    parseInt(String(d['2']), 10) === listItem.id
                ) {
                    periodTotal = parseInt(String(d['1']), 10);
                    break;
                }
            }

            newResult[keyName] = periodTotal;
            if (keyColor) {
                newResult[keyColor] = listItem.color ?? null;
            }
        });

        results.push(newResult);
    });

    return results;
};

export const transformLinkDataToBar = (data: RawDataItem[], kedo: Kedo): ChartDataItem[] => {
    const barData: ChartDataItem[] = [];
    let overallTotal = 0;

    data.forEach((item) => {
        const label = item.summary?.length ? item.summary : kedo.t('Unknown');
        const value = parseInt(String(item['1']), 10);

        barData.push({
            id: label,
            label,
            kedoId: item['2'] as number,
            value,
        });

        overallTotal += value;
    });

    barData.push({
        id: kedo.t('Total'),
        label: kedo.t('Total'),
        value: overallTotal,
    });

    return barData;
};

export const transformLinkDataToLine = (data: RawDataItem[], kedo: Kedo): LineChartDataItem[] => {
    const line: { x: string | number; y: number; kedoId?: number }[] = [];

    data.forEach((item) => {
        const label = item.summary?.length ? item.summary : kedo.t('Unknown');
        line.push({
            x: label,
            y: parseInt(String(item['1']), 10),
            kedoId: item['2'] as number,
        });
    });

    if (line.length === 0) return [];

    return [
        {
            id: kedo.t('Items'),
            color: 'hsl(335, 70%, 50%)',
            data: line,
        },
    ];
};

export const transformLinkDataToPie = (data: RawDataItem[], kedo: Kedo): ChartDataItem[] => {
    return data.map((item) => {
        const label = item.summary?.length ? item.summary : kedo.t('Unknown');
        return {
            id: label,
            label,
            kedoId: item['2'] as number,
            value: parseInt(String(item['1']), 10),
        };
    });
};

export const transformLinkDateBarData = (
    data: RawDataItem[],
    xKey: string,
    yKey: string,
    kVal: string
): BarChartDataItem[] => {
    const xItems = getUniqueFromData(data, xKey);
    const yItems = getUniqueFromData(data, yKey);
    const results: BarChartDataItem[] = [];

    xItems.forEach((xItem) => {
        const newResult: BarChartDataItem = { period: xItem };

        yItems.forEach((yItem) => {
            const keyName = yItem ?? 'Unknown';
            let periodTotal = 0;

            for (const d of data) {
                const dataRecord = d as Record<string, unknown>;
                if (
                    dataRecord[xKey] !== null &&
                    String(dataRecord[xKey]) === xItem &&
                    String(dataRecord[yKey]) === yItem
                ) {
                    periodTotal = parseInt(String(dataRecord[kVal]), 10);
                    break;
                }
            }

            newResult[keyName] = periodTotal;
        });

        results.push(newResult);
    });

    return results;
};

export const getUniqueDates = (data: RawDataItem[]): string[] => {
    const dates: string[] = [];

    data.forEach((item) => {
        if (item.yearAnddate !== null && !dates.includes(item.yearAnddate.toString())) {
            dates.push(item.yearAnddate.toString());
        }
    });

    return dates;
};

export const getUniqueFromData = (
    data: RawDataItem[] | Record<string, unknown>[],
    keyName: string
): string[] => {
    const uniques: string[] = [];

    data.forEach((item) => {
        const record = item as Record<string, unknown>;
        if (typeof record[keyName] === 'undefined') {
            if (!uniques.includes('Unknown')) {
                uniques.push('Unknown');
            }
        } else if (
            record[keyName] !== null &&
            typeof record[keyName]?.toString === 'function' &&
            !uniques.includes(record[keyName].toString())
        ) {
            uniques.push(record[keyName].toString());
        }
    });

    return uniques;
};

export const getTransformedKeys = (listField: ListField | null, kedo: Kedo): string[] => {
    if (!listField) return [];

    return listField.def_field.def_field_lists.map((item) =>
        kedo.env().translateItem(item, 'def_field_list')
    );
};

export const hasFieldColors = (listField: ListField | null): boolean => {
    if (!listField) return false;

    const defFieldLists = listField.def_field?.def_field_lists ?? [];

    return defFieldLists.length > 0 && Boolean(defFieldLists[0]?.color?.length);
};

export const getColorConfig = (
    scheme: string | null | undefined,
    hasColors: boolean
): ChartColorConfig => {
    if (scheme) return { scheme };
    if (hasColors) return { datum: 'data.color' };
    return { scheme: 'nivo' };
};

export const calculateTotal = (
    data: Record<string, unknown>[],
    xKey: string,
    unXItem: string,
    yKey: string,
    unYItem: string | null,
    vKey: string
): number => {
    const filteredData = data.filter(
        (dataItem) =>
            dataItem[xKey] === unXItem &&
            dataItem[yKey] !== null &&
            unYItem !== null &&
            String(dataItem[yKey]) === String(unYItem)
    );

    return filteredData.reduce((acc, dataItem) => acc + parseInt(String(dataItem[vKey]), 10), 0);
};

export const calculateTotalForColumn = (
    data: Record<string, unknown>[],
    yKey: string,
    unYItem: string,
    vKey: string
): number => {
    return data
        .filter((dataItem) => {
            const yValue = dataItem[yKey];
            return yValue !== undefined && yValue !== null && String(yValue) === String(unYItem);
        })
        .map((dataItem) => parseInt(String(dataItem[vKey]), 10))
        .reduce((prev, curr) => prev + curr, 0);
};
